import type { QueryKey } from '@tanstack/react-query';
import type { DateTime } from 'luxon';
import type { ScheduleFilters } from '@/pages/Schedule/contexts';
import type {
  ExperienceLayouts,
  FindDistrictsInput,
  GetLibraryInput,
  ListExperienceEventInput,
  SortDirection,
} from '@/types/gql.generated';

type CreateEntriesQueryKeyDateRange = {
  startDate: string;
  endDate: string;
};

/** Which position in an entries query key is the date range located? */
const ENTRIES_QUERY_KEY_DATE_RANGE_POSITION = 3;

// login/register pages
export const createAuthActionQueryKey = (email: string) => [
  'auth-action',
  email,
];

export const createProfileQueryKey = (): QueryKey => ['profile'];

export const createSchedulesQueryKey = (): QueryKey => ['schedule'];

export const createScheduleQueryKey = (scheduleId: string): QueryKey => [
  'schedule',
  scheduleId,
];

export const createScheduleTemplatesQueryKey = (): QueryKey => [
  'schedule-templates',
];

export const createScheduleJobQueryKey = (jobId: string): QueryKey => [
  'schedule-job',
  jobId,
];

export const createScheduleCollaboratorsQueryKey = (
  scheduleId: string
): QueryKey => ['schedule', scheduleId, 'collaborators'];

export const createEntriesQueryKey = (
  scheduleId?: string | null,
  dateRange?: CreateEntriesQueryKeyDateRange,
  filters?: ScheduleFilters
): QueryKey => {
  const key: unknown[] = ['schedule', scheduleId, 'entries'];
  if (dateRange) {
    key[ENTRIES_QUERY_KEY_DATE_RANGE_POSITION] = dateRange;
  }
  if (filters) {
    key[ENTRIES_QUERY_KEY_DATE_RANGE_POSITION + 1] = filters;
  }
  return key;
};

/**
 * Break a `queryKey` created by `createEntriesQueryKey` up into parts.
 * Note that this currently only parses the date range.
 */
export const parseCreateEntriesQueryKey = (queryKey: QueryKey) => {
  const isDateRange = (
    value: unknown
  ): value is CreateEntriesQueryKeyDateRange => {
    return (
      typeof value === 'object' &&
      value !== null &&
      'startDate' in value &&
      'endDate' in value
    );
  };
  const dateRange = queryKey[ENTRIES_QUERY_KEY_DATE_RANGE_POSITION];
  if (isDateRange(dateRange)) {
    return { dateRange };
  }
  return { dateRange: null };
};

// Export to calendar modal
export const createScheduleFeedSecretQueryKey = (
  scheduleId: string
): QueryKey => ['schedule', scheduleId, 'feed-secret'];

// Sources tab home
export const createScheduleSourcesQueryKey = (scheduleId: string): QueryKey => {
  return ['schedule', scheduleId, 'sources'];
};

// Sources tab -> source detail
export const createScheduleSourceQueryKey = (
  scheduleId: string,
  feedId: string | undefined,
  filters?: { startDate: DateTime; endDate: DateTime }
): QueryKey => {
  return [
    'schedule',
    scheduleId,
    'sources',
    {
      feedId,
      ...(filters && {
        startDate: filters.startDate.toISO(),
        endDate: filters.endDate.toISO(),
      }),
    },
  ];
};

// Sources tab -> add source modal
export const createGetInternalFeedsQueryKey = (
  scheduleId: string
): QueryKey => ['schedule', scheduleId, 'internal-feeds'];

export const createScheduleIcsFeedsQueryKey = (
  scheduleId: string
): QueryKey => ['schedule', scheduleId, 'ics-feeds'];

export const createVerifyTokenQueryKey = (token?: string): QueryKey => [
  'verify-reset-token',
  token,
];

// Members page & share modal
export const createScheduleMembersQueryKey = (
  scheduleId?: string
): QueryKey => ['schedule', scheduleId, 'members'];

// Edit entry message modal -> entry
// Entry detail page
export const createEntryQueryKey = (
  scheduleId: string,
  entryId: string | undefined
): QueryKey => ['schedule', scheduleId, 'entry', entryId];

// Entry modal -> messages
export const createEntryMessagesQueryKey = (
  scheduleId: string,
  entryId: string
): QueryKey => ['schedule', scheduleId, 'entry', entryId, 'messages'];

// Entry modal -> rsvps
export const createEntryInvitesQueryKey = (
  scheduleId: string,
  entryId: string
): QueryKey => ['schedule', scheduleId, 'entry', entryId, 'invites'];

// Message center
export const createScheduleEntryMessagesQueryKey = (
  scheduleId: string,
  sortDirection?: SortDirection
): QueryKey => {
  const keys = ['schedule', scheduleId, 'entry-messages'];
  if (sortDirection) {
    keys.push(sortDirection);
  }
  return keys;
};

// schedule snippets -> list view
export const createScheduleSnippetsQueryKey = (
  scheduleId: string
): QueryKey => ['schedule', scheduleId, 'snippets'];

// snippet page
export const createSnippetQueryKey = (
  snippetId?: string | undefined,
  userId?: string | undefined
): QueryKey => {
  const keys = ['snippet'];
  if (snippetId) {
    keys.push(snippetId);
  }
  // userId ensures the query won't be cached with a stale `scheduleId` property
  // when changing auth status while viewing a snippet
  if (userId) {
    keys.push('user', userId);
  }
  return keys;
};

// account modal
export const createIntegrationsQueryKey = (): QueryKey => ['integrations'];

// query to poll for the active entries job status in the add content modal
export const createAddContentJobResultsQueryKey = (
  scheduleId: string,
  entriesJobId?: string | null
): QueryKey => {
  const key = ['schedule', scheduleId, 'add-content', 'results'];
  if (entriesJobId) {
    key.push(entriesJobId);
  }
  return key;
};

// query that lists availability/conflict responses for a conflict schedule
export const createAvailabilityResponsesQueryKey = (
  scheduleId: string
): QueryKey => ['schedule', scheduleId, 'availability'];

export const createClassifyPromptQueryKey = (prompt: string): QueryKey => [
  'classify-prompt',
  prompt,
];

// magic tool
export const createGetMagicJobsQueryKey = (): QueryKey => ['magic', 'jobs'];

export const createGetMagicJobQueryKey = (jobId: string | null): QueryKey => [
  'magic',
  'job',
  jobId,
];

export const createGetMagicChatQueryKey = (chatId: string): QueryKey => [
  'magic',
  'chat',
  chatId,
];

export const createGetPrintableConfigInfoQueryKey = (): QueryKey => [
  'printable',
  'options',
];

export const createExperienceChildConfigsQueryKey = (): QueryKey => [
  'getExperienceChildConfigs',
];

export const createGetLayoutQueryKey = (
  layoutId: ExperienceLayouts
): QueryKey => ['layout', layoutId];

export const createMagicJobPageQueryKey = (
  slugOrId: string | undefined
): QueryKey => ['magic', 'page', slugOrId];

export const createScheduleHeaderImagesQueryKey = (): QueryKey => [
  'schedule',
  'header-images',
];

export const createExperiencesQueryKey = (): QueryKey => ['experiences'];

export const createExperienceQueryKey = (
  idOrSlug: string | undefined
): QueryKey => ['experience', idOrSlug];

// An AI-designed printable, rendered server-side against the live events.
// Without arguments it is the prefix that covers every experience and version.
export const createCustomArtifactQueryKey = (
  experienceId?: string,
  version?: number | null
): QueryKey =>
  experienceId
    ? ['customArtifact', experienceId, version ?? 'latest']
    : ['customArtifact'];

// The same, as a visitor to the planner's shared page sees it. Under the same
// prefix, so invalidating every custom artifact covers it too.
export const createPublishedCustomArtifactQueryKey = (
  parentSlug: string,
  childExperienceId: string
): QueryKey => ['customArtifact', childExperienceId, 'published', parentSlug];

// Published planner children, fetched anonymously through the parent's slug.
// Keyed apart from createExperienceQueryKey(childId) because the authed and
// published documents cache different response shapes ({ getExperience } vs
// { getPublishedChildExperience }); @normy keeps the child object itself in
// sync across both caches by id.
export const createPublishedChildExperienceQueryKey = (
  parentSlug: string | undefined,
  childExperienceId: string | undefined
): QueryKey => ['experience', parentSlug, 'child', childExperienceId];

// Without filters, returns the prefix key — useful for invalidating every
// cached entry for an experience regardless of which filter slice was fetched.
// With filters, returns a specific key so different filter sets are cached
// separately.
export const createExperienceEventsQueryKey = (
  idOrSlug: string | undefined,
  filters?: ListExperienceEventInput
): QueryKey =>
  filters === undefined
    ? ['experience', idOrSlug, 'events']
    : ['experience', idOrSlug, 'events', filters];

// Events queries are keyed by experience id in edit mode but by publishedSlug
// in the published view; this predicate matches both so an invalidation
// reaches whichever key the current provider used.
export const createExperienceEventsPredicate =
  (experience: { id: string; publishedSlug?: string | null }) =>
  (query: { queryKey: QueryKey }): boolean => {
    const [scope, key, sub] = query.queryKey as (string | undefined)[];
    return (
      scope === 'experience' &&
      sub === 'events' &&
      (key === experience.id || key === experience.publishedSlug)
    );
  };

// Single event fetched by id. Nested under the experience's events prefix so
// the prefix invalidation above refreshes it alongside the event lists.
export const createExperienceEventQueryKey = (
  experienceId: string,
  eventId: string | undefined
): QueryKey => ['experience', experienceId, 'events', 'event', eventId];

export const createEventMetadataQueryKey = (
  experienceId: string,
  eventId: string
): QueryKey => [
  'experience',
  experienceId,
  'events',
  'event',
  eventId,
  'metadata',
];

// Matches every per-(experience, event) metadata override slot written by
// useUpdateEventMetadata, optionally narrowed to one experience. Used to drop
// those slots when the server has changed metadata behind the client's back:
// the slot is a skipToken query that never refetches, so until it is removed it
// shadows the metadata on freshly refetched events.
export const isEventMetadataQueryKey = (
  queryKey: QueryKey,
  experienceId?: string
): boolean =>
  queryKey.length === 6 &&
  queryKey[0] === 'experience' &&
  queryKey[2] === 'events' &&
  queryKey[3] === 'event' &&
  queryKey[5] === 'metadata' &&
  (experienceId === undefined || queryKey[1] === experienceId);

// Event edit modal -> messages
export const createEventMessagesQueryKey = (
  experienceId: string,
  eventId: string
): QueryKey => [
  'experience',
  experienceId,
  'events',
  'event',
  eventId,
  'messages',
];

// Customize mode -> experience-wide text message list
export const createExperienceEventMessagesQueryKey = (
  experienceId: string
): QueryKey => ['experience', experienceId, 'event-messages'];

// Event edit modal -> message recipient picker
export const createEventMessageRecipientsQueryKey = (
  experienceId: string
): QueryKey => ['experience', experienceId, 'message-recipients'];

// Matches the recipient picker for EVERY experience. Who can receive a text is
// decided by org membership and phone numbers, which are edited in settings —
// and settings has no experience context to build a single key from.
export const isEventMessageRecipientsQueryKey = (queryKey: QueryKey): boolean =>
  queryKey[0] === 'experience' && queryKey[2] === 'message-recipients';

// Trailing undefineds are dropped so the argless form (['library']) prefix-
// matches every library query — with or without an event id or input — for
// invalidation and cache writes (an explicit `undefined` slot would fail
// TanStack's partial matching against filtered keys like ['library', input]).
export const createLibraryQueryKey = (
  eventId?: string,
  input?: GetLibraryInput
): QueryKey =>
  (eventId ? ['library', eventId, input] : ['library', input]).filter(
    (part) => part !== undefined
  );

export const createExperienceCollaboratorsQueryKey = (
  experienceId: string
): QueryKey => ['experience', experienceId, 'collaborators'];

export const createThemesQueryKey = (experienceId: string): QueryKey => [
  'experience',
  experienceId,
  'themes',
];

export const createSlugAvailabilityKey = (slug: string): QueryKey => [
  'slug-availability',
  slug,
];

export const createExperienceSlugQueryKey = (
  experienceId: string
): QueryKey => ['experience', experienceId, 'slug'];

export const createCanvaDesignsQueryKey = (
  integrationId: string,
  options: { cursor?: string } = {}
): QueryKey => ['canva', integrationId, 'designs', options];

export const createCollectionQueryKey = (baseId?: string) =>
  ['collection', baseId] as const;

export const createTemplateAccountExperiencesQueryKey = (): QueryKey => [
  'templateAccountExperiences',
];

export const createDistrictsQueryKey = (
  input: FindDistrictsInput
): QueryKey => ['districts', input];

export const createExperienceUpdateRequestersQueryKey = (
  experienceId: string
): QueryKey => ['experience-update-requesters', experienceId];

export const createAppConfigQueryKey = (): QueryKey => ['app-config'];

export const createAccountDeletionPreviewQueryKey = (): QueryKey => [
  'account-deletion-preview',
];

// Settings modal -> Organizations tab
export const createOrganizationMembersQueryKey = (
  organizationId: string
): QueryKey => ['organizations', organizationId, 'members'];

export const createOrganizationInvitesQueryKey = (
  organizationId: string
): QueryKey => ['organizations', organizationId, 'invites'];

export const createPendingInvitationsQueryKey = (): QueryKey => [
  'pending-invitations',
];
